import { Col, Row, Table } from 'antd'
import { ColumnProps } from 'antd/lib/table'
import * as R from 'ramda'
import React from 'react'
import { connect } from 'react-redux'
import { firestoreConnect, isLoaded, withFirestore } from 'react-redux-firebase'
import { Link } from 'react-router-dom'
import { compose, withProps } from 'recompose'

import { dataFromOrdered, fsCollections } from '../../helpers/firestore'
import { rankRecords } from '../../helpers/results'
import routes from '../../helpers/routes'
import { TournamentPlayer } from '../../schemas/Player'
import { TenantId } from '../../schemas/Tenant'
import { TournamentId } from '../../schemas/Tournament'
import { spinnerWhileLoading } from '../higher-order/spinner'

import './TournamentStatistics.css'

type TournamentStatisticsRecord = {
  rank: string
  player: TournamentPlayer
  value: number
}

type TournamentStatisticsInnerProps = TournamentStatisticsOuterProps & {
  aces: TournamentStatisticsRecord[]
  acesInRow: TournamentStatisticsRecord[]
}

const statisticsColumns = (
  tenant: TenantId,
  tournamentId: TournamentId,
): Array<ColumnProps<TournamentStatisticsRecord>> => [
  {
    title: 'Rank',
    dataIndex: 'rank',
  },
  {
    title: 'Player',
    key: 'fullName',
    render: (text, record) => (
      <div style={{ textAlign: 'left' }}>
        <Link to={routes.playerResults(tenant, tournamentId, record.player.id)}>
          {record.player.firstName} {record.player.lastName}
        </Link>
      </div>
    ),
  },
  {
    title: 'Value',
    dataIndex: 'value',
  },
]

const acesInRowTitle = () => 'Aces In Row'

const acesTitle = () => 'Aces'

const getRowKey = (record: TournamentStatisticsRecord) => record.player.id

const TournamentStatistics: React.FunctionComponent<TournamentStatisticsInnerProps> = ({
  tenant,
  tournamentId,
  aces,
  acesInRow,
}) => {
  const columns = statisticsColumns(tenant, tournamentId)

  return (
    <div className="TournamentStatistics">
      <Row gutter={24}>
        <Col lg={12} md={24}>
          <Table
            rowKey={getRowKey}
            bordered
            pagination={false}
            title={acesInRowTitle}
            columns={columns}
            dataSource={acesInRow}
          />
        </Col>
        <Col lg={12} md={24}>
          <Table
            rowKey={getRowKey}
            bordered
            pagination={false}
            title={acesTitle}
            columns={columns}
            dataSource={aces}
          />
        </Col>
      </Row>
    </div>
  )
}

type TournamentStatisticsItem = {
  player: TournamentPlayer
  totalAces: number
  maxAcesInRow: number
}

const getSingleStatisticsItem = (statKey: string) => (
  tournamentStatistics: TournamentStatisticsItem[],
) => {
  const groupedByRankStatisticItems = (R.pipe(
    R.filter((item) => R.pathOr(0, [statKey], item) > 0),
    // @ts-ignore TODO
    R.map(({ player, ...stats }) => ({
      player,
      value: R.pathOr(0, [statKey], stats),
    })),
    R.sortWith([R.descend(R.prop('value'))]),
    R.groupWith((l, r) => l.value === r.value),
  ) as FIXME)(tournamentStatistics)

  return rankRecords(0, groupedByRankStatisticItems)
}

type TournamentStatisticsOuterProps = {
  tenant: TenantId
  tournamentId: TournamentId
}

export default compose<TournamentStatisticsOuterProps, TournamentStatisticsOuterProps>(
  withFirestore,
  firestoreConnect<FIXME>(({ tenant, tournamentId }) => [
    { collection: fsCollections.tournamentPlayers(tenant, tournamentId) },
    { collection: fsCollections.tournamentCombinedResults(tenant, tournamentId) },
  ]),
  connect(({ firestore }: FIXME, { tenant, tournamentId }: FIXME) => ({
    tournamentPlayers: firestore.ordered[fsCollections.tournamentPlayers(tenant, tournamentId)],
    tournamentCombinedResultsMap: dataFromOrdered(
      firestore.ordered[fsCollections.tournamentCombinedResults(tenant, tournamentId)],
    ),
  })),
  spinnerWhileLoading(
    ({ tournamentPlayers, tournamentCombinedResultsMap }) =>
      !isLoaded(tournamentPlayers, tournamentCombinedResultsMap),
  ),
  withProps(({ tournamentPlayers, tournamentCombinedResultsMap }) => {
    const allCombinedResults: TournamentStatisticsItem[] = tournamentPlayers.map((player) => {
      const selectedResults = R.path([player.id, 'withFinal'], tournamentCombinedResultsMap)

      return {
        player,
        totalAces: R.pathOr(0, ['totalAces'], selectedResults),
        maxAcesInRow: R.pathOr(0, ['maxAcesInRow'], selectedResults),
      }
    })

    const aces = getSingleStatisticsItem('totalAces')(allCombinedResults)
    const acesInRow = getSingleStatisticsItem('maxAcesInRow')(allCombinedResults)

    return {
      aces,
      acesInRow,
    }
  }),
)(TournamentStatistics)



// WEBPACK FOOTER //
// ./src/components/tournament/TournamentStatistics.tsx